/**
 * API响应验证工具
 * 用于验证API响应结构的完整性和正确性
 */

/**
 * 验证API响应结构
 * @param {Object} response - API响应对象
 * @returns {Object} 验证结果 { isValid: boolean, error?: string }
 */
export function validateApiResponse(response) {
  // 检查响应是否存在
  if (!response) {
    return {
      isValid: false,
      error: 'API响应为空'
    }
  }

  // 检查必需的字段
  const requiredFields = ['code', 'msg', 'data']
  const missingFields = []

  for (const field of requiredFields) {
    if (!(field in response)) {
      missingFields.push(field)
    }
  }

  if (missingFields.length > 0) {
    return {
      isValid: false,
      error: `API响应缺少必需字段: ${missingFields.join(', ')}`
    }
  }

  // 检查code字段类型
  if (typeof response.code !== 'number') {
    return {
      isValid: false,
      error: 'API响应code字段必须是数字类型'
    }
  }

  // 检查msg字段类型（可以是字符串或null）
  if (response.msg !== null && typeof response.msg !== 'string') {
    return {
      isValid: false,
      error: 'API响应msg字段必须是字符串或null'
    }
  }

  return {
    isValid: true
  }
}

/**
 * 验证用户列表响应数据结构
 * @param {Object} data - 响应的data字段
 * @returns {Object} 验证结果 { isValid: boolean, error?: string }
 */
export function validateUserListData(data) {
  if (!data || typeof data !== 'object') {
    return {
      isValid: false,
      error: '用户列表数据必须是对象类型'
    }
  }

  // 检查分页相关字段
  const requiredPaginationFields = ['total', 'pages', 'size', 'page', 'list']
  const missingFields = []

  for (const field of requiredPaginationFields) {
    if (!(field in data)) {
      missingFields.push(field)
    }
  }

  if (missingFields.length > 0) {
    return {
      isValid: false,
      error: `用户列表数据缺少必需字段: ${missingFields.join(', ')}`
    }
  }

  // 验证数字类型字段
  const numberFields = ['total', 'pages', 'size', 'page']
  for (const field of numberFields) {
    if (typeof data[field] !== 'number' || data[field] < 0) {
      return {
        isValid: false,
        error: `字段 ${field} 必须是非负数`
      }
    }
  }

  // 验证list字段
  if (!Array.isArray(data.list)) {
    return {
      isValid: false,
      error: 'list字段必须是数组类型'
    }
  }

  return {
    isValid: true
  }
}

/**
 * 根据HTTP状态码生成用户友好的错误消息
 * @param {number} statusCode - HTTP状态码
 * @param {string} defaultMessage - 默认错误消息
 * @returns {string} 用户友好的错误消息
 */
export function getErrorMessage(statusCode, defaultMessage = '请求失败') {
  const errorMessages = {
    400: '请求参数错误',
    401: '登录已过期，请重新登录',
    403: '权限不足，无法访问该资源',
    404: '请求的资源不存在',
    408: '请求超时，请稍后重试',
    429: '请求过于频繁，请稍后重试',
    500: '服务器内部错误，请稍后重试',
    502: '网关错误，请稍后重试',
    503: '服务暂时不可用，请稍后重试',
    504: '网关超时，请稍后重试'
  }

  return errorMessages[statusCode] || defaultMessage
}

/**
 * 检查是否为网络错误
 * @param {Error} error - 错误对象
 * @returns {boolean} 是否为网络错误
 */
export function isNetworkError(error) {
  return !error.response && (
    error.code === 'NETWORK_ERROR' ||
    error.code === 'ECONNABORTED' ||
    error.message.includes('Network Error') ||
    error.message.includes('timeout')
  )
}

/**
 * 检查是否为服务器临时不可用
 * @param {Error} error - 错误对象
 * @returns {boolean} 是否为服务器临时不可用
 */
export function isServerUnavailable(error) {
  if (!error.response) return false
  
  const { status } = error.response
  return status === 502 || status === 503 || status === 504
}

/**
 * 生成重试建议消息
 * @param {Error} error - 错误对象
 * @returns {string} 重试建议消息
 */
export function getRetryMessage(error) {
  if (isNetworkError(error)) {
    return '网络连接失败，请检查网络连接后重试'
  }
  
  if (isServerUnavailable(error)) {
    return '服务暂时不可用，请稍后重试'
  }
  
  return '操作失败，请重试'
}